import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { ActiveDataRoom, DataRoomFolder } from '../../shared/models';

interface DocumentFolderActionButtonProps {
	isEmployee: boolean;
	folder: DataRoomFolder;
	activeDeals: ActiveDataRoom[] | undefined;
	onEdit: (folder: DataRoomFolder) => void;
	onDelete: (folder: DataRoomFolder) => void;
	onSend: (folder: DataRoomFolder) => void;
	onCopyDocuments: (folder: DataRoomFolder) => void;
}

type FolderActionItem = keyof typeof FolderActionItemMap;
const FolderActionItemMap = {
	Edit: 'Edit',
	EmailLender: 'Email Lender',
	Delete: 'Delete',
	CopyDocuments: 'Copy Documents',
};

export const DocumentFolderActionButton: React.FC<DocumentFolderActionButtonProps> = ({
	isEmployee,
	folder,
	activeDeals,
	onEdit,
	onDelete,
	onSend,
	onCopyDocuments,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const canCopyDocuments =
		activeDeals && activeDeals.length > 0 && (folder.name == 'Root' || folder.name == 'Data Room Folder');

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: FolderActionItem) => {
		switch (action) {
			case 'Edit':
				onEdit(folder);
				break;
			case 'EmailLender':
				onSend(folder);
				break;
			case 'Delete':
				onDelete(folder);
				break;
			case 'CopyDocuments':
				onCopyDocuments(folder);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: FolderActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
										{FolderActionItemMap['Edit']}
									</MenuItem>
									{isEmployee && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'EmailLender')}>
											{FolderActionItemMap['EmailLender']}
										</MenuItem>
									)}
									{canCopyDocuments && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'CopyDocuments')}>
											{FolderActionItemMap['CopyDocuments']}
										</MenuItem>
									)}
									<MenuItem
										disabled={!folder.canDelete}
										onClick={event => handleMenuItemClick(event, 'Delete')}
									>
										{FolderActionItemMap['Delete']}
									</MenuItem>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
